/**
 * "Themes from recent calls" (Stage 6, insights flag; Admin / CallCentreAdmin or CallCentre.AI.Use).
 *
 * Themes, prayer themes and coaching hints the AI found in the last 7, 14 or 30 days of call notes. Pastoral notes
 * are never read, and only aggregates come back (no names). The server keeps each period for 6 hours; Refresh asks
 * for a new one. `ai_unavailable` hides the card; any other failure shows inline and as a toast.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconBulb, IconHeartHandshake, IconRefresh, IconSparkles, IconMessages } from '@tabler/icons-react';
import { useCallCentreAdminService } from '@actschurch/ccms-sdk';
import { Stack, Group, Button, Text, Select, Alert, Badge, Paper, Skeleton, SimpleGrid } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  SectionCard,
  InlineEmpty,
  callCentreKeys,
  formatStampDateTime,
  plural,
  CALL_CENTRE_AI_FAILED_TEXT,
  callCentreAiErrorKind,
  useCallCentreAiUnavailable,
} from '../components';

const PERIODS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
];

export function AiThemesCard() {
  const admin = useCallCentreAdminService();
  const queryClient = useQueryClient();
  const aiOff = useCallCentreAiUnavailable();
  const [days, setDays] = useState(7);

  const insights = useQuery({
    queryKey: callCentreKeys.aiInsights(days),
    queryFn: () => admin.getAiInsights(days, false),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const refresh = useMutation({
    mutationFn: () => admin.getAiInsights(days, true),
    onSuccess: (view) => queryClient.setQueryData(callCentreKeys.aiInsights(days), view),
    onError: (err) => {
      if (callCentreAiErrorKind(err) === 'unavailable') {
        aiOff();
        return;
      }
      toast.error("Couldn't refresh the themes", CALL_CENTRE_AI_FAILED_TEXT);
    },
  });

  // Switched off since the page opened: hide the card (the availability is read again). Any other failure: a toast too.
  const unavailable = !!insights.error && callCentreAiErrorKind(insights.error) === 'unavailable';
  useEffect(() => {
    if (unavailable) aiOff();
  }, [unavailable, aiOff]);
  const loadFailedAt = insights.error && !unavailable ? insights.errorUpdatedAt : 0;
  useEffect(() => {
    if (loadFailedAt) toast.error("Couldn't load the themes", CALL_CENTRE_AI_FAILED_TEXT);
  }, [loadFailedAt]);
  if (unavailable) return null;

  const data = insights.data;
  const failed = (insights.isError && !data) || refresh.isError;
  const busy = insights.isLoading || refresh.isPending;
  const themes = data?.themes ?? [];
  const prayer = (data?.prayerThemes ?? []).filter((p) => p && p.trim());
  const hints = (data?.coachingHints ?? []).filter((h) => h && h.trim());

  return (
    <SectionCard
      icon={<IconSparkles size={18} />}
      color="violet"
      title="Themes from recent calls"
      description="What callers are hearing, found by AI in the call notes. Pastoral notes are never read."
      helpId="callcentre.dashboard.ai-themes"
      actions={
        <Group gap="xs" wrap="nowrap">
          <Select
            size="xs"
            w={140}
            data={PERIODS}
            value={String(days)}
            onChange={(v) => v && setDays(Number(v))}
            allowDeselect={false}
            aria-label="Period"
            disabled={busy}
          />
          <Button size="compact-sm" variant="light" leftSection={<IconRefresh size={14} />} loading={refresh.isPending} disabled={insights.isLoading} onClick={() => refresh.mutate()}>
            Refresh
          </Button>
        </Group>
      }
    >
      {failed && (
        <Alert color="orange" mb="sm" p="xs">
          <Text size="sm">{CALL_CENTRE_AI_FAILED_TEXT}</Text>
        </Alert>
      )}

      {busy && !data ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Reading the call notes. This can take a little while.
          </Text>
          <Skeleton height={64} radius="md" />
          <Skeleton height={64} radius="md" />
        </Stack>
      ) : data ? (
        data.notesConsidered === 0 ? (
          <InlineEmpty
            icon={<IconMessages size={22} />}
            title={`No call notes in the last ${plural(data.days || days, 'day')}`}
            description="Themes show here once callers write notes on their calls."
          />
        ) : (
          <Stack gap="md">
            {themes.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                {themes.map((t, i) => (
                  <Paper key={`${t.title}-${i}`} withBorder radius="md" p="sm">
                    <Stack gap={4}>
                      <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
                        <Text size="sm" fw={600}>
                          {t.title}
                        </Text>
                        {t.mentions > 0 && (
                          <Badge size="sm" variant="light" color="violet" styles={{ root: { textTransform: 'none', flexShrink: 0 } }}>
                            {`About ${plural(t.mentions, 'note')}`}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" c="dimmed">
                        {t.summary}
                      </Text>
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            ) : (
              <Text size="sm" c="dimmed">
                No clear themes stood out in these notes.
              </Text>
            )}

            {(prayer.length > 0 || hints.length > 0) && (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                {prayer.length > 0 && (
                  <Stack gap={6}>
                    <Group gap={6}>
                      <IconHeartHandshake size={16} color="var(--mantine-color-pink-6)" />
                      <Text size="sm" fw={600}>
                        Prayer themes
                      </Text>
                    </Group>
                    <Group gap={6}>
                      {prayer.map((p, i) => (
                        <Badge key={i} variant="light" color="pink" styles={{ root: { textTransform: 'none' } }}>
                          {p}
                        </Badge>
                      ))}
                    </Group>
                  </Stack>
                )}
                {hints.length > 0 && (
                  <Stack gap={6}>
                    <Group gap={6}>
                      <IconBulb size={16} color="var(--mantine-color-yellow-6)" />
                      <Text size="sm" fw={600}>
                        Coaching hints
                      </Text>
                    </Group>
                    <Stack gap={4}>
                      {hints.map((h, i) => (
                        <Text key={i} size="sm">
                          {h}
                        </Text>
                      ))}
                    </Stack>
                  </Stack>
                )}
              </SimpleGrid>
            )}
          </Stack>
        )
      ) : null}

      {data && (
        <Text size="xs" c="dimmed" mt="sm">
          Based on {plural(data.notesConsidered, 'note')}, generated {formatStampDateTime(data.generatedAt, 'just now')}. Suggested by AI, so check before acting on it.
        </Text>
      )}
    </SectionCard>
  );
}
